import { createSelector } from '@reduxjs/toolkit';
import { getCsidFromAlert } from 'features/alerts/common/AlertHelpers';
import { Alert, ArtifactType } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import { IAlertDetails, IAlerts, IPaginationData } from './alerts.types';
import { IAlertsState } from './alertsState';

export const getAlertDetails: (state: IAlertsState, alertId: string) => IAlertDetails | undefined = createSelector(
  [(state: IAlertsState) => state.alertDetailsMap, (state: IAlertsState, alertId: string) => alertId],
  (alertDetailsMap: { [id: string]: IAlertDetails }, alertId: string) => alertDetailsMap[alertId.toLowerCase()]
);

export const getAlerts: (state: IAlertsState) => {
  allAlerts: IAlertDetails[];
  activeAlerts: IAlertDetails[];
  inactiveAlerts: IAlertDetails[];
  dashboardAlerts: IAlertDetails[];
} = createSelector(
  [
    (state: IAlertsState) => state.allAlerts.alertIds,
    (state: IAlertsState) => state.dashboardAlerts.alertIds,
    (state: IAlertsState) => state.alertDetailsMap,
  ],
  (allAlertIds: string[], dashboardAlertIds: string[], alertDetailsMap: { [id: string]: IAlertDetails }) => {
    const allAlerts = allAlertIds.map((id) => alertDetailsMap[id.toLowerCase()]).filter((alert) => Boolean(alert));
    const dashboardAlerts = dashboardAlertIds.map((id) => alertDetailsMap[id.toLowerCase()]).filter((alert) => Boolean(alert));
    return {
      allAlerts,
      activeAlerts: allAlerts.filter((a) => a.isActive),
      inactiveAlerts: allAlerts.filter((a) => !a.isActive),
      dashboardAlerts,
    };
  }
);

export const getActiveAlerts: (state: IAlertsState) => IAlertDetails[] = createSelector(
  [getAlerts],
  ({ activeAlerts }) => activeAlerts
);

export const getInactiveAlerts: (state: IAlertsState) => IAlertDetails[] = createSelector(
  [getAlerts],
  ({ inactiveAlerts }) => inactiveAlerts
);

export const getDashboardAlerts: (state: IAlertsState) => IAlertDetails[] = createSelector(
  [getAlerts],
  ({ dashboardAlerts }) => dashboardAlerts
);

export const getAllAlertsForArtifactIdAndType: (
  state: IAlertsState,
  artifactIds: string[],
  artifactType: ArtifactType,
  csid?: string
) => IAlertDetails[] = createSelector(
  [
    getAlerts,
    (state: IAlertsState, artifactIds: string[]) => artifactIds,
    (state: IAlertsState, artifactIds: string[], artifactType: ArtifactType) => artifactType,
    (state: IAlertsState, artifactIds: string[], artifactType: ArtifactType, csid?: string) => csid,
  ],
  ({ allAlerts }, artifactIds, artifactType, csid) => {
    return allAlerts.filter((a) => {
      if (csid && getCsidFromAlert(a) !== csid) {
        return false;
      }

      return artifactIds.includes(a.artifactId) && a.artifactType === artifactType;
    });
  }
);

export const hasMoreAlerts: (state: IAlertsState) => boolean = createSelector(
  [(state: IAlertsState) => state.allAlerts],
  (allAlerts: IAlerts) => allAlerts.fetchPageApiStatus !== ApiCallResultStatus.SUCCESS
);

export const hasMoreDashboardAlerts: (state: IAlertsState) => boolean = createSelector(
  [(state: IAlertsState) => state.dashboardAlerts],
  (dashboardAlerts: IAlerts) => dashboardAlerts.fetchPageApiStatus !== ApiCallResultStatus.SUCCESS
);

export const getAlertUpdateStatus: (state: IAlertsState, alertId: string) => ApiCallResultStatus = createSelector(
  [getAlertDetails],
  (alertDetail: IAlertDetails | undefined) => alertDetail?.updateAlertsApiStatus || ApiCallResultStatus.DEFAULT
);

export const hasMoreActiveAlerts: (state: IAlertsState) => boolean = createSelector(
  [(state: IAlertsState) => state.allAlerts.paginationData, getActiveAlerts],
  (paginationData: IPaginationData | undefined, activeAlerts: Alert[]) => {
    if (!paginationData) {
      // if paginationData is undefined, it means no fetch call has been made yet, so return true
      return true;
    }
    // if number of loaded active alerts is less than total active items count, then return true
    return activeAlerts.length < paginationData.totalActiveItemCount;
  }
);

export const hasMoreInactiveAlerts: (state: IAlertsState) => boolean = createSelector(
  [(state: IAlertsState) => state.allAlerts.paginationData, getInactiveAlerts],
  (paginationData: IPaginationData | undefined, inactiveAlerts: Alert[]) => {
    if (!paginationData) {
      // if paginationData is undefined, it means no fetch call has been made yet, so return true
      return true;
    }
    // if number of loaded inactive alerts is less than total inactive items count, then return true
    return inactiveAlerts.length < paginationData.totalInactiveItemCount;
  }
);
